import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '../index';
import { getBackendUrl } from '../../../services/config/backendUrl';
import { getAuthHeaders } from '../../../services/auth/authHeaders';

export const useAnalytics = (campaignId: number) => {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const refreshParam = refresh ? '?refresh=true' : '';
      const data = await apiClient.get(`/api/campaigns/${campaignId}/analytics${refreshParam}`);
      setAnalytics(data);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const fetchFailedReasons = useCallback(async (limit: number, statuses: string[]) => {
    const statusParam = statuses.length > 0 ? `&statuses=${statuses.join(',')}` : '';
    return apiClient.get(`/api/campaigns/${campaignId}/failed-reasons?limit=${limit}${statusParam}`);
  }, [campaignId]);

  const analyzeFailures = useCallback(async () => {
    return apiClient.post(`/api/campaigns/${campaignId}/analyze-failures`);
  }, [campaignId]);

  return {
    analytics,
    loading,
    error,
    fetchAnalytics,
    fetchFailedReasons,
    analyzeFailures,
  };
};

// Fetch URL-level click summary for a campaign
export const useCampaignClickSummary = (campaignId: number) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClickSummary = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/campaign/${campaignId}/clicks`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchClickSummary();
  }, [fetchClickSummary]);

  return { data, loading, error, fetchClickSummary };
};

// Fetch detailed click data for CSV export
export const useCampaignClickDetails = (campaignId: number) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClickDetails = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/campaign/${campaignId}/clicks/detail`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchClickDetails();
  }, [fetchClickDetails]);

  return { data, loading, error, fetchClickDetails };
};

// CSV download helper for click details
export const downloadClickDetailsCSV = (data: any[], campaignName: string = 'campaign') => {
  const header = ['Email', 'URL', 'Clicked At'];
  const rows = data.map(row => [
    row.email,
    `"${row.url}"`,
    new Date(row.clickedAt).toISOString()
  ]);

  const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `click-details-${campaignName}-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};